/** Capture HTTP traffic via a global fetch interceptor and show it in a debug panel. */

import { copyTextToClipboard } from './formatListClipboard';
import { escapeHtml } from './escapeHtml';

export type HttpLogEntry = {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody?: string;
  status?: number;
  statusText?: string;
  responseHeaders?: Record<string, string>;
  responseBody?: string;
  durationMs?: number;
  error?: string;
};

const MAX_ENTRIES = 300;
const MAX_BODY_CHARS = 48_000;

const entries: HttpLogEntry[] = [];
const listeners = new Set<() => void>();

let interceptorInstalled = false;
let modalEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let selectedId: string | null = null;

function notify(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function pushEntry(entry: HttpLogEntry): void {
  entries.unshift(entry);
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  notify();
}

function headersToRecord(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    out[key] = redactHeaderValue(key, value);
  });
  return out;
}

function redactHeaderValue(name: string, value: string): string {
  const lower = name.toLowerCase();
  if (lower === 'authorization') {
    if (/^bearer\s+/i.test(value)) return 'Bearer ***';
    return '***';
  }
  return value;
}

function truncate(text: string, max = MAX_BODY_CHARS): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}\n… [truncated ${text.length - max} chars]`;
}

async function bodyPreview(res: Response): Promise<string> {
  const ct = (res.headers.get('content-type') ?? '').toLowerCase();
  try {
    if (ct.includes('image/') || ct.includes('octet-stream') || ct.includes('application/pdf')) {
      const buf = await res.arrayBuffer();
      return `[binary ${buf.byteLength} bytes]`;
    }
    const text = await res.text();
    if (!text) return '[empty]';
    try {
      const parsed = JSON.parse(text) as unknown;
      return truncate(JSON.stringify(parsed, null, 2));
    } catch {
      return truncate(text);
    }
  } catch (err) {
    return `[could not read body: ${err instanceof Error ? err.message : String(err)}]`;
  }
}

function requestBodyPreview(body: RequestInit['body']): string | undefined {
  if (body == null) return undefined;
  if (typeof body === 'string') return truncate(body);
  if (body instanceof URLSearchParams) return truncate(body.toString());
  if (body instanceof ArrayBuffer) return `[ArrayBuffer ${body.byteLength} bytes]`;
  if (ArrayBuffer.isView(body)) return `[binary ${body.byteLength} bytes]`;
  if (body instanceof Blob) return `[Blob ${body.size} bytes, type=${body.type || 'unknown'}]`;
  return `[${Object.prototype.toString.call(body)}]`;
}

function resolveRequest(input: RequestInfo | URL, init?: RequestInit): {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
} {
  if (input instanceof Request) {
    const merged = new Headers(input.headers);
    if (init?.headers) new Headers(init.headers).forEach((v, k) => merged.set(k, v));
    const reqBody = requestBodyPreview(init?.body);
    return {
      method: (init?.method ?? input.method).toUpperCase(),
      url: input.url,
      headers: headersToRecord(merged),
      ...(reqBody !== undefined ? { body: reqBody } : {}),
    };
  }
  const url = input instanceof URL ? input.href : input;
  const headers = init?.headers ? new Headers(init.headers) : new Headers();
  const reqBody = requestBodyPreview(init?.body);
  return {
    method: (init?.method ?? 'GET').toUpperCase(),
    url,
    headers: headersToRecord(headers),
    ...(reqBody !== undefined ? { body: reqBody } : {}),
  };
}

export function getHttpLogEntries(): readonly HttpLogEntry[] {
  return entries;
}

export function clearHttpLog(): void {
  entries.length = 0;
  selectedId = null;
  notify();
}

export function installHttpDebugInterceptor(): void {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const req = resolveRequest(input, init);
    const entry: HttpLogEntry = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      method: req.method,
      url: req.url,
      requestHeaders: req.headers,
    };
    if (req.body !== undefined) entry.requestBody = req.body;
    pushEntry(entry);

    const started = performance.now();
    try {
      const response = await nativeFetch(input, init);
      entry.status = response.status;
      entry.statusText = response.statusText;
      entry.responseHeaders = headersToRecord(response.headers);
      entry.durationMs = Math.round(performance.now() - started);

      try {
        entry.responseBody = await bodyPreview(response.clone());
      } catch (err) {
        entry.responseBody = `[response body error: ${err instanceof Error ? err.message : String(err)}]`;
      }

      notify();
      return response;
    } catch (err) {
      entry.durationMs = Math.round(performance.now() - started);
      entry.error = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      notify();
      throw err;
    }
  };
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour12: false, fractionalSecondDigits: 3 } as Intl.DateTimeFormatOptions);
}

function statusClass(entry: HttpLogEntry): string {
  if (entry.error) return 'http-debug-status--error';
  if (entry.status == null) return '';
  if (entry.status >= 500) return 'http-debug-status--error';
  if (entry.status >= 400) return 'http-debug-status--warn';
  return 'http-debug-status--ok';
}

function statusLabel(entry: HttpLogEntry): string {
  if (entry.error) return entry.error;
  if (entry.status != null) return `${entry.status} ${entry.statusText ?? ''}`.trim();
  return '…';
}

function formatEntryForClipboard(entry: HttpLogEntry): string {
  const lines: string[] = [
    `Time: ${new Date(entry.timestamp).toISOString()}`,
    `Method: ${entry.method}`,
    `URL: ${entry.url}`,
    `Duration: ${entry.durationMs != null ? `${entry.durationMs}ms` : '—'}`,
  ];
  if (entry.status != null) {
    lines.push(`Status: ${entry.status} ${entry.statusText ?? ''}`.trim());
  }
  if (entry.error) lines.push(`Error: ${entry.error}`);
  lines.push('', 'Request headers:', JSON.stringify(entry.requestHeaders, null, 2));
  if (entry.requestBody != null) {
    lines.push('', 'Request body:', entry.requestBody);
  }
  if (entry.responseHeaders) {
    lines.push('', 'Response headers:', JSON.stringify(entry.responseHeaders, null, 2));
  }
  if (entry.responseBody != null) {
    lines.push('', 'Response body:', entry.responseBody);
  }
  return lines.join('\n');
}

function renderDetail(entry: HttpLogEntry): string {
  const blocks: string[] = [
    `<div class="http-debug-detail-row"><span class="http-debug-detail-label">URL</span><pre class="http-debug-pre">${escapeHtml(entry.url)}</pre></div>`,
    `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Request headers</span><pre class="http-debug-pre">${escapeHtml(JSON.stringify(entry.requestHeaders, null, 2))}</pre></div>`,
  ];
  if (entry.requestBody != null) {
    blocks.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Request body</span><pre class="http-debug-pre">${escapeHtml(entry.requestBody)}</pre></div>`
    );
  }
  if (entry.responseHeaders) {
    blocks.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Response headers</span><pre class="http-debug-pre">${escapeHtml(JSON.stringify(entry.responseHeaders, null, 2))}</pre></div>`
    );
  }
  if (entry.responseBody != null) {
    blocks.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Response body</span><pre class="http-debug-pre http-debug-pre--body">${escapeHtml(entry.responseBody)}</pre></div>`
    );
  }
  if (entry.error) {
    blocks.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Error</span><pre class="http-debug-pre http-debug-status--error">${escapeHtml(entry.error)}</pre></div>`
    );
  }
  return blocks.join('');
}

function renderList(): void {
  if (!listEl) return;
  if (entries.length === 0) {
    listEl.innerHTML = '<p class="http-debug-empty">No HTTP traffic captured yet.</p>';
    return;
  }

  listEl.innerHTML = entries
    .map(entry => {
      const open = entry.id === selectedId;
      const duration = entry.durationMs != null ? `${entry.durationMs}ms` : '—';
      return `
        <article class="http-debug-entry${open ? ' http-debug-entry--open' : ''}" data-id="${escapeHtml(entry.id)}">
          <div class="http-debug-entry-top">
            <button type="button" class="http-debug-entry-summary">
              <span class="http-debug-entry-time">${escapeHtml(formatTime(entry.timestamp))}</span>
              <span class="http-debug-entry-method">${escapeHtml(entry.method)}</span>
              <span class="http-debug-entry-status ${statusClass(entry)}">${escapeHtml(statusLabel(entry))}</span>
              <span class="http-debug-entry-duration">${escapeHtml(duration)}</span>
              <span class="http-debug-entry-url">${escapeHtml(entry.url)}</span>
            </button>
            <button type="button" class="http-debug-entry-copy btn-ghost-dark btn-sm" aria-label="Copy request details">Copy</button>
          </div>
          ${open ? `<div class="http-debug-entry-detail">${renderDetail(entry)}</div>` : ''}
        </article>
      `;
    })
    .join('');

  listEl.querySelectorAll<HTMLElement>('.http-debug-entry-summary').forEach(btn => {
    btn.addEventListener('click', () => {
      const row = btn.closest('.http-debug-entry');
      const id = row?.getAttribute('data-id') ?? null;
      selectedId = id && selectedId !== id ? id : null;
      renderList();
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('.http-debug-entry-copy').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      void copyHttpLogEntry(btn);
    });
  });
}

async function copyHttpLogEntry(btn: HTMLButtonElement): Promise<void> {
  const id = btn.closest('.http-debug-entry')?.getAttribute('data-id');
  const entry = id ? entries.find(e => e.id === id) : undefined;
  if (!entry) return;
  const label = btn.textContent;
  try {
    await copyTextToClipboard(formatEntryForClipboard(entry));
    btn.textContent = 'Copied';
    window.setTimeout(() => {
      btn.textContent = label ?? 'Copy';
    }, 1500);
  } catch {
    btn.textContent = 'Failed';
    window.setTimeout(() => {
      btn.textContent = label ?? 'Copy';
    }, 1500);
  }
}

function closeModal(): void {
  if (!modalEl) return;
  modalEl.classList.add('hidden');
  document.body.style.overflow = '';
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'http-debug-modal';
  modal.className = 'http-debug-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'HTTP debug log');
  modal.innerHTML = `
    <div class="http-debug-modal-backdrop" aria-hidden="true"></div>
    <div class="http-debug-modal-sheet">
      <header class="http-debug-modal-header">
        <h2 class="http-debug-modal-title">HTTP debug log</h2>
        <div class="http-debug-modal-actions">
          <button type="button" class="btn-ghost-dark btn-sm" id="http-debug-clear">Clear</button>
          <button type="button" class="btn-primary btn-sm" id="http-debug-close">Close</button>
        </div>
      </header>
      <p class="http-debug-modal-hint">Requests and responses from fetch (GitHub API). Authorization headers are redacted.</p>
      <div class="http-debug-list" id="http-debug-list"></div>
    </div>
  `;

  listEl = modal.querySelector('#http-debug-list');
  modal.querySelector('.http-debug-modal-backdrop')!.addEventListener('click', closeModal);
  modal.querySelector('#http-debug-close')!.addEventListener('click', closeModal);
  modal.querySelector('#http-debug-clear')!.addEventListener('click', () => {
    clearHttpLog();
    renderList();
  });

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

export function mountHttpDebugModal(): void {
  ensureModal();
  subscribe(() => {
    if (modalEl && !modalEl.classList.contains('hidden')) renderList();
  });
}

export function openHttpDebugModal(): void {
  const modal = ensureModal();
  renderList();
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}
